import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { StepperStep } from './StepperStep';
import { resolveStepStatus, isConnectorFilled } from './stepperStatus';
import type { StepperProps } from './stepperTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Stepper component for multi-step analysis processes
 *
 * @example
 * ```tsx
 * <Stepper
 *   steps={[
 *     { label: 'Upload', status: 'completed' },
 *     { label: 'Processing', status: 'current' },
 *     { label: 'Analysis', status: 'upcoming' },
 *     { label: 'Results', status: 'upcoming' },
 *   ]}
 * />
 * ```
 */
export const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  ({ steps, currentStep, orientation = 'horizontal', size = 'md', className, ...props }, ref) => {
    const { t } = useTranslation();
    const isVertical = orientation === 'vertical';

    return (
      <div
        ref={ref}
        className={cn('flex', isVertical ? 'flex-col' : 'items-center', className)}
        role="list"
        aria-label={t('a11y.progressSteps')}
        {...props}
      >
        {steps.map((step, index) => (
          <StepperStep
            key={index}
            step={step}
            status={resolveStepStatus(index, step, currentStep)}
            index={index}
            isLast={index === steps.length - 1}
            isVertical={isVertical}
            size={size}
            connectorFilled={isConnectorFilled(steps, index, currentStep)}
          />
        ))}
      </div>
    );
  }
);

Stepper.displayName = 'Stepper';
